import React, { useEffect, useRef } from 'react';
import { ChatMessage, ToolCall, LangGraphToolCall } from '../types';
import { MessageBubble } from './message-bubble';
import { useLangGraph } from '../hooks/use-langgraph';
import { useCopilot } from '../hooks/use-copilot';

interface MessageListProps {
  messages: ChatMessage[];
  isLoading: boolean;
}

export const MessageList: React.FC<MessageListProps> = ({ messages, isLoading }) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { thread } = useLangGraph();
  const { setInputMessage } = useCopilot();

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Determine if we should show the welcome page
  const showWelcome = messages.length === 0 && !isLoading;

  return (
    <div className="flex-1 overflow-y-auto p-4 bg-white">
      {showWelcome ? (
        <div className="flex flex-col items-start h-full px-8 pt-8 pb-4 overflow-y-auto">
          <div className="text-left mb-8">
            <h2 className="text-3xl font-medium mb-1 bg-clip-text text-transparent bg-gradient-to-r from-primary to-mid-blue">Hello, Pamekitti!</h2>
            <p className="text-2xl text-gray-500 font-normal">How can I help you today?</p>
          </div>

          <div className="w-full max-w-md space-y-3 mb-4">
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <div className="flex items-start">
                <div className="flex-shrink-0 mr-3">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-gray-500">
                    <rect width="18" height="18" x="3" y="3" rx="2" ry="2"></rect>
                    <path d="M7 7h10"></path>
                    <path d="M7 12h10"></path>
                    <path d="M7 17h10"></path>
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-800">Summary of AHU Page</p>
                  <p className="text-xs text-gray-500 mt-1">Only AHU 5 and 6 are running with no faults. Energy usage is 355.1 kW, with 22.5 kWh consumed today and no energy savings recorded this month. Average room temperature is high at 31.9°C with 65.5% humidity. Autopilot is inactive on all units. Issues identified include invalid AHU settings, lack of energy savings, and high room temperatures. Actions needed are to enable Autopilot, optimize AHU settings, and improve room temperature control.</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-2">
              <button
                className="flex items-center text-left p-3 rounded-lg bg-gray-50 transition-all duration-200 hover:bg-gray-100"
                onClick={() => {
                  setInputMessage('Generate a realtime report summarizing building operations');
                  // Focus the textarea after setting the message
                  setTimeout(() => {
                    const textarea = document.querySelector('textarea');
                    if (textarea) textarea.focus();
                  }, 0);
                }}
              >
                <div className="flex-shrink-0 mr-3">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-gray-500">
                    <path d="M3 3v18h18"></path>
                    <path d="m19 9-5 5-4-4-3 3"></path>
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-800">Generate Realtime Report</p>
                  <p className="text-xs text-gray-500 mt-1">Report Current Building Operations</p>
                </div>
              </button>

              <button
                className="flex items-center text-left p-3 rounded-lg bg-gray-50 transition-all duration-200 hover:bg-gray-100"
                onClick={() => {
                  setInputMessage('Analyze building data for energy efficiency insights');
                  // Focus the textarea after setting the message
                  setTimeout(() => {
                    const textarea = document.querySelector('textarea');
                    if (textarea) textarea.focus();
                  }, 0);
                }}
              >
                <div className="flex-shrink-0 mr-3">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-gray-500">
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="m15 9-6 6"></path>
                    <path d="m9 9 6 6"></path>
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-800">Analyze for insights</p>
                  <p className="text-xs text-gray-500 mt-1">Generate insights or trends for today's data</p>
                </div>
              </button>

              <button
                className="flex items-center text-left p-3 rounded-lg bg-gray-50 transition-all duration-200 hover:bg-gray-100"
                onClick={() => {
                  setInputMessage('What can this Copilot do to help with building automation?');
                  // Focus the textarea after setting the message
                  setTimeout(() => {
                    const textarea = document.querySelector('textarea');
                    if (textarea) textarea.focus();
                  }, 0);
                }}
              >
                <div className="flex-shrink-0 mr-3">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-gray-500">
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                    <path d="M12 17h.01"></path>
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-800">AltoGPT Walkthrough</p>
                  <p className="text-xs text-gray-500 mt-1">What can AltoGPT do?</p>
                </div>
              </button>
            </div>
          </div>

          {/* <div className="flex justify-center w-full mb-8">
            <button
              className="flex items-center text-sm text-gray-600 hover:text-gray-800 transition-all duration-200 p-1.5 rounded-lg"
              onClick={() => {
                setInputMessage('test-xs');
                // Focus the textarea after setting the message
                setTimeout(() => {
                  const textarea = document.querySelector('textarea');
                  if (textarea) textarea.focus();
                }, 0);
              }}
            >
              More suggestions
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ml-1">
                <path d="m9 18 6-6-6-6"></path>
              </svg>
            </button>
          </div> */}

          {/* Disclaimer moved to message-input.tsx */}
        </div>
      ) : (
        <>
          {/* Filter out tool messages and only show human, AI, and system messages */}
          {messages.filter(msg => msg.type !== 'tool').map((message) => {
            // Process message to extract tool calls and reasoning if they exist
            let processedMessage = { ...message };

            // Check if the message has tool_calls in additional_kwargs (from LangGraph)
            if (message.type === 'ai' && message.additional_kwargs) {
              // Extract tool calls if they exist
              if (message.additional_kwargs.tool_calls &&
                Array.isArray(message.additional_kwargs.tool_calls) &&
                message.additional_kwargs.tool_calls.length > 0) {

                // Extract tool calls from additional_kwargs
                const toolCalls: ToolCall[] = message.additional_kwargs.tool_calls.map((toolCall: LangGraphToolCall) => ({
                  id: toolCall.id || `tool-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
                  name: toolCall.function?.name || 'unknown',
                  args: toolCall.function?.arguments ?
                    // Try to parse JSON, fallback to empty object if it fails
                    (() => {
                      try {
                        return JSON.parse(toolCall.function?.arguments || '{}');
                      } catch (e) {
                        console.warn('Failed to parse tool call arguments:', e);
                        return {};
                      }
                    })() : {}
                }));

                // Add tool calls to the processed message
                processedMessage.toolCalls = toolCalls;
              }

              // Extract reasoning if it exists in metadata
              if (message.additional_kwargs.metadata?.thinking) {
                processedMessage.reasoning = {
                  content: message.additional_kwargs.metadata.thinking,
                  duration: message.additional_kwargs.metadata.thinking_duration || 0
                };
              }

              // Look for tool results in the message history
              if (processedMessage.toolCalls && processedMessage.toolCalls.length > 0) {
                // Find tool messages that correspond to the tool calls
                // Include both visible and filtered-out tool messages
                const allMessages = thread?.messages?.map((msg: any) => {
                  // Convert LangGraph message to our format
                  const langGraphMsg = msg as any;
                  if (msg.type === 'tool' || langGraphMsg.additional_kwargs?.tool_call_id) {
                    return {
                      id: msg.id || `msg-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
                      timestamp: Date.now(),
                      type: 'tool',
                      content: typeof msg.content === 'string' ? msg.content : JSON.stringify(msg.content),
                      additional_kwargs: {
                        tool_call_id: langGraphMsg.additional_kwargs?.tool_call_id || langGraphMsg.tool_call_id,
                        status: langGraphMsg.status || 'success'
                      }
                    };
                  }
                  return null;
                }).filter(Boolean) || [];

                const toolMessages = [...messages, ...allMessages].filter(msg =>
                  msg && (msg.type === 'tool' ||
                    (msg.additional_kwargs && msg.additional_kwargs.tool_call_id))
                );

                // Match tool results with tool calls
                if (toolMessages.length > 0) {
                  processedMessage.toolCalls = processedMessage.toolCalls.map(toolCall => {
                    // Find matching tool message by tool_call_id
                    const matchingToolMessage = toolMessages.find(toolMsg => {
                      if (!toolMsg) return false;
                      const toolMsgId = toolMsg.additional_kwargs?.tool_call_id;
                      return toolMsgId === toolCall.id;
                    });

                    if (matchingToolMessage) {
                      return {
                        ...toolCall,
                        result: matchingToolMessage.content,
                        status: matchingToolMessage.additional_kwargs?.status || 'success'
                      };
                    }

                    return toolCall;
                  });

                  // If we have reasoning, append tool call results to it
                  if (processedMessage.toolCalls.some(tc => tc.result)) {
                    // Create a summary of tool calls and results
                    const toolCallSummary = processedMessage.toolCalls
                      .filter(tc => tc.result)
                      .map(tc => `Tool: ${tc.name}\nArgs: ${JSON.stringify(tc.args)}\nResult: ${tc.result}`)
                      .join('\n\n');

                    // If we don't have reasoning yet, create it
                    if (!processedMessage.reasoning) {
                      processedMessage.reasoning = {
                        content: `Tool Call Results:\n\n${toolCallSummary}`,
                        duration: 0
                      };
                    }
                    // Otherwise append to existing reasoning
                    else if (processedMessage.reasoning.content) {
                      // Check if the reasoning already contains the tool call results
                      if (!processedMessage.reasoning.content.includes(toolCallSummary)) {
                        processedMessage.reasoning.content += '\n\n' + toolCallSummary;
                      }
                    }
                  }
                }
              }
            }

            return (
              <MessageBubble
                key={processedMessage.id}
                message={processedMessage}
              />
            );
          })}
          {isLoading && (
            <div className="flex justify-start mb-6">
              <div className="flex space-x-2 px-3 py-2">
                <div className="w-2 h-2 rounded-full bg-gray-300 animate-pulse" style={{ animationDelay: '0ms' }}></div>
                <div className="w-2 h-2 rounded-full bg-gray-300 animate-pulse" style={{ animationDelay: '150ms' }}></div>
                <div className="w-2 h-2 rounded-full bg-gray-300 animate-pulse" style={{ animationDelay: '300ms' }}></div>
              </div>
            </div>
          )}
        </>
      )}
      <div ref={messagesEndRef} className="h-4" />
    </div>
  );
};
